import Section from '../../../components/Main/Section/Section';
import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import { v4 as uuid } from 'uuid';
import { useTranslation } from 'react-i18next';
import ActionBar from '../../../components/ActionBar/ActionBar';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import { AuthContext } from '../../../providers/AuthProvider';
import { ISurveysEntry } from '../../../shared/interfaces/ISurveysEntry';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import SurveyService from '../../../services/SurveyService';
import { useNavigate, useParams } from 'react-router-dom';
import { Add, Check, OpenInNew } from '@mui/icons-material';
import { ICustomSurveyQuestion } from '../../../shared/interfaces/ICustomSurveyQuestion';
import SurveyQuestion from './SurveyQuestion';
import { SortableList } from '../../../components/Dnd/SortableList';
import SurveyUtils from '../../../shared/SurveyUtils';

const SurveyQuestions = () => {
  const { hasRole } = useContext(AuthContext);
  const { setCurrentView } = useContext(CurrentViewContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const params = useParams();
  const [survey, setSurvey] = useState<ISurveysEntry | null>(null);
  const [questions, setQuestions] = useState<ICustomSurveyQuestion[]>([]);
  const [newType, setNewType] = useState<string | null>('CUSTOMSINGLE');
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    setCurrentView(t('survey_tool'));
    document.title = `Reputativ ${t('survey_tool')} `;
  }, [t]);

  const reload = async () => {
    const controller = new AbortController();
    await getSurveys(controller.signal);
  };

  const reorderAndReload = async (deletedQid: string | undefined) => {
    reorderQuestions(questions, deletedQid);
    await reload();
  };

  const getSurveys = async (signal: AbortSignal) => {
    const response = await SurveyService.getSurvey(setLoading, signal);
    if (response) {
      for (let i = 0; i < response.length; i++) {
        if (response[i].id == params.id) {
          setSurvey(response[i]);
          await getQuestions(response[i], signal);
        }
      }
    }
  };

  const getQuestions = async (ls: ISurveysEntry, signal: AbortSignal) => {
    const response = await SurveyService.getQuestions(ls.id, ls.type, setLoading, signal);
    if (response) {
      // set the id so we can use the base item for sorting
      response.forEach((q: ICustomSurveyQuestion) => (q.id = q.questionId!));
      setQuestions(response);
    }
  };

  const createExampleQuestion = (type: string | null) => {
    let newPosition = 1;
    if (!type) {
      type = 'CUSTOMSINGLE';
    }
    if (questions) {
      newPosition = questions.length + 1;
    }
    // this id is only for sorting
    const id = uuid();
    switch (type) {
      case 'CUSTOMSCALENPS':
        return {
          id: id,
          type: type,
          question: 'Wie bewerten Sie unsere Dienstleistungen?',
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: [],
        };
      case 'CUSTOMTEXT':
        return {
          id: id,
          type: type,
          question: 'Bitte nennen Sie uns Ihre Meinung zu ...',
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: [],
        };
      case 'CUSTOMYESNO':
        return {
          id: id,
          type: type,
          question: 'Würden Sie uns weiterempfehlen?',
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: [{ label: 'Ja' }, { label: 'Nein' }],
        };
      default:
        return {
          id: id,
          type: type,
          question: 'Welche Option bevorzugen Sie?',
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: [{ label: 'eins' }, { label: 'zwei' }, { label: 'drei' }],
        };
    }
  };

  const handleAdd = async () => {
    setLoading(true);
    const dataset = createExampleQuestion(newType);

    await SurveyService.addQuestion(dataset.surveyId, survey?.type || '', dataset);
    await reload();
  };

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      await getSurveys(controller.signal);
    })();

    return () => {
      controller.abort();
    };
  }, []);

  const onDragEnd = (questionsReordered: any) => {
    reorderQuestions(questionsReordered);
  };

  const reorderQuestions = async (questionsReordered: any, deletedQid?: string) => {
    let position = 1;
    const keep: any[] = [];
    for (let i = 0; i < questionsReordered.length; i++) {
      const currentQuestionId = questionsReordered[i].questionId || '';
      if (deletedQid && deletedQid === currentQuestionId) {
        continue;
      }
      questionsReordered[i].position = position;
      await SurveyService.updateQuestion(
        survey?.type!,
        survey?.id!,
        currentQuestionId,
        questionsReordered[i]
      );
      position++;
      keep.push(questionsReordered[i]);
    }
    setQuestions(keep);
  };

  const renderQuestions = () => {
    if (!survey) {
      return null;
    }
    if (!questions) {
      return null;
    }

    return (
      <SortableList
        items={questions}
        onChange={onDragEnd}
        renderItem={(item) => (
          <SortableList.Item id={item.id}>
            <SurveyQuestion
              survey={survey}
              question={item}
              reorderAndReload={reorderAndReload}
              reload={reload}
            />
            <SortableList.DragHandle />
          </SortableList.Item>
        )}
      />
    );
  };

  return survey && hasRole('can-use-survey') ? (
    <Section>
      <ContentContainer>
        <LeftContainer>
          <ActionBar
            sx={{ mb: theme.spacing(2) }}
            children={<span>{survey.label}</span>}
          />

          {renderQuestions()}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(2),
              my: theme.spacing(2),
            }}
          >
            <FormControl
              size="small"
              sx={{ minWidth: '17.75rem' }}
            >
              <InputLabel id="select-label">{t('select_survey_question_type')}</InputLabel>
              <Select
                labelId="select-label"
                label={t('select_survey_question_type')}
                value={newType || ''}
                onChange={(e: SelectChangeEvent) => {
                  setNewType(e.target.value);
                }}
              >
                <MenuItem value={'CUSTOMSINGLE'}>{t('survey_question_customsingle')}</MenuItem>
                <MenuItem value={'CUSTOMDROPDOWN'}>{t('survey_question_customdropdown')}</MenuItem>
                <MenuItem value={'CUSTOMYESNO'}>{t('survey_question_customyesno')}</MenuItem>
                <MenuItem value={'CUSTOMSCALENPS'}>{t('survey_question_customscalenps')}</MenuItem>
                <MenuItem value={'CUSTOMTEXT'}>{t('survey_question_customtext')}</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAdd}
            >
              {t('add_question')}
            </Button>
            <Button
              href={
                `${process.env.REACT_APP_FRONTEND_BASE_URL}/${SurveyUtils.getPathByType(
                  survey.type
                )}/${survey.slug}?preview=true` ||
                window.location.href + `/${survey.type}/${survey.slug}?preview=true`
              }
              target="_blank"
              variant="contained"
              startIcon={<OpenInNew />}
            >
              {t('preview')}
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/research/surveytool/guide/')}
              startIcon={<Check />}
            >
              {t('complete')}
            </Button>
          </Box>
        </LeftContainer>
      </ContentContainer>
    </Section>
  ) : null;
};

export default SurveyQuestions;
